import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  Send, User, RotateCcw, AlertTriangle,
  FileText, ShieldCheck, Scale, BarChart3, BookOpen,
  MessageSquare, ClipboardCheck, FileSearch, Users, AlertCircle,
  ChevronDown, Zap, Sparkles, Paperclip, X, File, FileSpreadsheet, Image
} from 'lucide-react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// NAVI Avatar URL
const NAVI_AVATAR = "https://customer-assets.emergentagent.com/job_risk-engine-pro-2/artifacts/0rppkc2h_image.png";

// Supported file types
const SUPPORTED_FILE_TYPES = {
  '.pdf': { icon: FileText, color: 'red', name: 'PDF' },
  '.docx': { icon: FileText, color: 'blue', name: 'Word' },
  '.doc': { icon: FileText, color: 'blue', name: 'Word' },
  '.xlsx': { icon: FileSpreadsheet, color: 'green', name: 'Excel' },
  '.xls': { icon: FileSpreadsheet, color: 'green', name: 'Excel' },
  '.csv': { icon: FileSpreadsheet, color: 'emerald', name: 'CSV' },
  '.txt': { icon: File, color: 'slate', name: 'Text' },
  '.md': { icon: File, color: 'purple', name: 'Markdown' },
  '.png': { icon: Image, color: 'pink', name: 'Image' },
  '.jpg': { icon: Image, color: 'pink', name: 'Image' },
  '.jpeg': { icon: Image, color: 'pink', name: 'Image' }
};

// NAVI Modes with descriptions
const NAVI_MODES = [
  { 
    id: 'chat', 
    name: 'Interactive Chat', 
    icon: MessageSquare,
    description: 'General compliance discussion and guidance',
    color: 'cyan'
  },
  { 
    id: 'assessment', 
    name: 'Assessment Mode', 
    icon: ClipboardCheck,
    description: 'Probing questions to assess compliance posture',
    color: 'blue'
  },
  { 
    id: 'document_review', 
    name: 'Document Review', 
    icon: FileSearch,
    description: 'Critical review of policies and procedures',
    color: 'purple'
  },
  { 
    id: 'advisory', 
    name: 'Advisory Mode', 
    icon: Users,
    description: 'Strategic compliance advice for leadership',
    color: 'emerald'
  },
  { 
    id: 'breach_response', 
    name: 'Breach Response', 
    icon: AlertCircle,
    description: 'Aggressive mode for non-compliance incidents',
    color: 'red'
  }
];

// Compliance Themes
const COMPLIANCE_THEMES = [
  { 
    id: 'general', 
    name: 'General Compliance', 
    icon: ShieldCheck,
    color: 'cyan',
    description: 'Overall compliance posture and regulatory obligations'
  },
  { 
    id: 'aml_cft', 
    name: 'AML/CFT/PF', 
    icon: Scale,
    color: 'amber',
    description: 'Anti-Money Laundering, Counter-Terrorist Financing, Proliferation Financing'
  },
  { 
    id: 'cybersecurity', 
    name: 'Cybersecurity', 
    icon: AlertCircle,
    color: 'red',
    description: 'Cyber threats, data protection, incident response'
  },
  { 
    id: 'it_governance', 
    name: 'IT Governance', 
    icon: FileSearch,
    color: 'blue',
    description: 'IT controls, system security, technology risk'
  },
  { 
    id: 'conduct', 
    name: 'Conduct', 
    icon: Users,
    color: 'purple',
    description: 'Conduct risk, treating customers fairly, FAIS compliance'
  },
  { 
    id: 'anti_bribery', 
    name: 'Anti-Bribery', 
    icon: AlertTriangle,
    color: 'orange',
    description: 'Bribery prevention, corruption controls, gifts & hospitality'
  },
  { 
    id: 'emerging_risk', 
    name: 'Emerging Risk', 
    icon: Zap,
    color: 'pink',
    description: 'New technologies, AI risk, crypto assets, RegTech'
  },
  { 
    id: 'governance', 
    name: 'Governance', 
    icon: BookOpen,
    color: 'emerald',
    description: 'Board oversight, King V, accountability frameworks'
  },
  { 
    id: 'risk_management', 
    name: 'Risk Management', 
    icon: BarChart3,
    color: 'indigo',
    description: 'Enterprise risk, risk appetite, control effectiveness'
  },
  { 
    id: 'global', 
    name: 'Global Compliance', 
    icon: FileText,
    color: 'teal',
    description: 'Cross-border, FATF, international standards'
  }
];

// NAVI Avatar Component
const NaviAvatar = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-14 h-14'
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden border-2 border-amber-500/50 shadow-lg shadow-amber-500/20 flex-shrink-0 ${className}`}>
      <img 
        src={NAVI_AVATAR} 
        alt="NAVI" 
        className="w-full h-full object-cover"
      />
    </div>
  );
};

// Chat message component with improved formatting
const ChatMessage = ({ message, isUser, mode, breachAnalysis }) => {
  const getModeColor = () => {
    if (mode === 'breach_response' || breachAnalysis?.severity === 'HIGH') {
      return 'border-red-500/30 bg-gradient-to-br from-red-950/50 to-red-900/20';
    }
    if (breachAnalysis?.severity === 'MEDIUM') {
      return 'border-amber-500/30 bg-gradient-to-br from-amber-950/50 to-amber-900/20';
    }
    return 'border-slate-700/50 bg-gradient-to-br from-slate-800/80 to-slate-900/50';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex gap-4 ${isUser ? 'flex-row-reverse' : ''}`}
    >
      {/* Avatar */}
      {isUser ? (
        <div className="w-10 h-10 rounded-full bg-cyan-500/20 border-2 border-cyan-500/30 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-cyan-400" />
        </div>
      ) : (
        <NaviAvatar size="md" />
      )}
      
      <div className={`flex-1 max-w-[80%] min-w-0 overflow-hidden ${isUser ? 'text-right' : ''}`}>
        {/* Breach/Mode indicator for NAVI messages */}
        {!isUser && breachAnalysis?.severity && breachAnalysis.severity !== 'NONE' && (
          <motion.div 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-t-xl text-xs font-semibold mb-0 ${
              breachAnalysis.severity === 'HIGH' 
                ? 'bg-red-500/20 text-red-400 border border-red-500/30 border-b-0' 
                : 'bg-amber-500/20 text-amber-400 border border-amber-500/30 border-b-0'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            {breachAnalysis.severity === 'HIGH' ? '⚠️ Breach Response Mode Activated' : '⚡ Compliance Concern Detected'}
          </motion.div>
        )}
        
        {/* Message bubble */}
        <div className={`p-5 rounded-2xl ${isUser ? 'rounded-tr-md' : 'rounded-tl-md'} ${
          isUser 
            ? 'bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border border-cyan-500/30 text-white' 
            : `${getModeColor()} text-white border`
        }`}>
          {/* NAVI name badge for NAVI messages */}
          {!isUser && (
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700/50">
              <span className="text-amber-400 font-semibold text-sm">NAVI</span>
              <span className="px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded text-[10px] text-amber-400/80 font-mono">
                AI Compliance Officer
              </span>
            </div>
          )}
          
          {/* Message content with markdown */}
          <div className="navi-message-content text-sm leading-relaxed break-words overflow-hidden" style={{ wordBreak: 'break-word' }}>
            {isUser ? (
              <p className="text-white">{message.content}</p>
            ) : (
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-lg font-bold text-white mt-4 mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" />{children}</h1>,
                  h2: ({children}) => <h2 className="text-base font-bold text-white mt-4 mb-2">{children}</h2>,
                  h3: ({children}) => <h3 className="text-sm font-semibold text-amber-400 mt-4 mb-2 uppercase tracking-wide">{children}</h3>,
                  strong: ({children}) => <strong className="font-semibold text-white">{children}</strong>,
                  em: ({children}) => <em className="text-amber-300/90 not-italic">{children}</em>,
                  ul: ({children}) => <ul className="space-y-2 my-3 ml-1">{children}</ul>,
                  ol: ({children}) => <ol className="space-y-2 my-3 ml-1 list-decimal list-inside">{children}</ol>,
                  li: ({children}) => (
                    <li className="flex items-start gap-2 text-slate-200">
                      <span className="text-amber-400 mt-1">•</span>
                      <span className="flex-1">{children}</span>
                    </li>
                  ),
                  p: ({children}) => <p className="my-2 text-slate-200 leading-relaxed break-words">{children}</p>,
                  blockquote: ({children}) => (
                    <blockquote className="border-l-2 border-amber-500/50 pl-4 my-3 text-slate-300 italic bg-amber-500/5 py-2 rounded-r">
                      {children}
                    </blockquote>
                  ),
                  code: ({children}) => (
                    <code className="bg-slate-700/50 px-1.5 py-0.5 rounded text-amber-300 text-xs font-mono break-all">
                      {children}
                    </code>
                  ),
                  a: ({href, children}) => (
                    <a href={href} className="text-cyan-400 hover:text-cyan-300 underline" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
        </div>
        
        {/* Timestamp and mode */}
        <div className={`flex items-center gap-2 text-xs text-slate-500 mt-1.5 ${isUser ? 'justify-end' : ''}`}>
          <span>{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          {!isUser && mode && mode !== 'chat' && (
            <>
              <span className="text-slate-600">•</span>
              <span className={`text-${NAVI_MODES.find(m => m.id === mode)?.color || 'slate'}-400`}>
                {NAVI_MODES.find(m => m.id === mode)?.name || mode}
              </span>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Typing indicator with NAVI avatar
const TypingIndicator = ({ mode }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex gap-4"
  >
    <NaviAvatar size="md" />
    <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/50 border border-slate-700/50 rounded-2xl rounded-tl-md p-4">
      <div className="flex items-center gap-3">
        <span className="text-xs text-amber-400 font-medium">NAVI is thinking</span>
        <div className="flex gap-1">
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: 0 }}
            className="w-2 h-2 bg-amber-400 rounded-full"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }}
            className="w-2 h-2 bg-amber-400 rounded-full"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: 0.4 }}
            className="w-2 h-2 bg-amber-400 rounded-full"
          />
        </div>
      </div>
    </div>
  </motion.div>
);

const NaviPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedMode, setSelectedMode] = useState('chat');
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [agenticContext, setAgenticContext] = useState(null);
  const [showKnowledgePanel, setShowKnowledgePanel] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load NAVI knowledge base on mount
  useEffect(() => {
    const loadKnowledgeBase = async () => {
      try {
        const response = await axios.get(`${API}/navi/agentic/knowledge-base`);
        setKnowledgeBase(response.data);
      } catch (err) {
        console.error('Failed to load NAVI knowledge base:', err);
      }
    };
    loadKnowledgeBase();
  }, []);

  // NAVI intelligent welcome message
  const getWelcomeMessage = (hasHistory = false) => ({
    id: 'welcome',
    content: `Good day${user?.name ? `, ${user.name}` : ''}. I'm **NAVI**, your AI Compliance Officer.

I operate as your **compliance officer**, **regulator challenger**, and **strategic risk advisor**. I don't just answer questions — I help you think critically about your compliance posture.

### How I Work
- I ask **probing questions** to understand your actual practices
- I **challenge assumptions** and test control effectiveness
- I **identify gaps** a regulator would question
- I provide **risk-based recommendations**

### My Expertise
- **AML/CFT Risk** (FICA)
- **Conduct Risk** (FAIS/COFI)
- **Data Privacy** (POPIA)
- **Cybersecurity** & IT Governance

${hasHistory ? "*I remember our previous discussions. Let's continue strengthening your compliance posture.*" : "*What compliance area would you like to explore?*"}`,
    isUser: false,
    timestamp: new Date().toISOString(),
    mode: 'chat'
  });

  // Load conversation history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await axios.get(`${API}/navi/history`);
        const history = response.data.messages || [];
        
        const formattedHistory = history.map((msg, idx) => ({
          id: `history-${idx}`,
          content: msg.content,
          isUser: msg.role === 'user',
          timestamp: msg.timestamp,
          mode: msg.mode
        }));
        
        setMessages([getWelcomeMessage(history.length > 0), ...formattedHistory]);
      } catch {
        // Failed to load history - show welcome message
        setMessages([getWelcomeMessage(false)]);
      } finally {
        setHistoryLoading(false);
      }
    };
    
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleClearHistory = async () => {
    try {
      await axios.delete(`${API}/navi/history`);
      toast.success('Conversation history cleared');
      setMessages([getWelcomeMessage(false)]);
    } catch {
      toast.error('Failed to clear history');
    }
  };

  // File selection handler
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_FILE_TYPES[ext]) {
      toast.error(`Unsupported file type. Supported: ${Object.keys(SUPPORTED_FILE_TYPES).join(', ')}`);
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setSelectedFile(file);
  };

  // File upload handler
  const handleFileUpload = async () => {
    if (!selectedFile) return;
    
    setUploadingFile(true);
    setLoading(true);
    
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    const fileInfo = SUPPORTED_FILE_TYPES[ext];
    
    // Add user message showing file upload
    const userMessage = {
      id: Date.now().toString(),
      content: `📎 **Document Upload:** ${selectedFile.name}\n\n${input || 'Please review this document.'}`,
      isUser: true,
      timestamp: new Date().toISOString(),
      hasAttachment: true,
      attachment: {
        name: selectedFile.name,
        type: fileInfo?.name || 'File',
        size: selectedFile.size
      }
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('message', input);
      formData.append('mode', 'document_review');
      
      const response = await axios.post(`${API}/navi/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const naviResponse = {
        id: (Date.now() + 1).toString(),
        content: response.data.response,
        isUser: false,
        timestamp: new Date().toISOString(),
        mode: 'document_review',
        breachAnalysis: response.data.breach_analysis
      };
      
      setMessages(prev => [...prev, naviResponse]);
      setSelectedFile(null);
      setInput('');
      
      if (response.data.breach_analysis?.severity && response.data.breach_analysis.severity !== 'NONE') {
        toast.warning(response.data.breach_analysis.message, {
          duration: 5000,
          icon: <AlertTriangle className="w-4 h-4" />
        });
      }
      
    } catch {
      toast.error('Failed to upload document');
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        content: "I apologize, but I couldn't process that document. Please ensure it's a valid file and try again.",
        isUser: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setUploadingFile(false);
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSend = async (query = input) => {
    // If there's a file selected, upload it instead
    if (selectedFile) {
      handleFileUpload();
      return;
    }
    
    if (!query.trim() || loading) return;

    // Build the message with theme context if selected
    const themeContext = selectedTheme 
      ? COMPLIANCE_THEMES.find(t => t.id === selectedTheme) 
      : null;
    
    const messageWithContext = themeContext 
      ? `[${themeContext.name}] ${query}`
      : query;

    const userMessage = {
      id: Date.now().toString(),
      content: query,
      isUser: true,
      timestamp: new Date().toISOString(),
      theme: selectedTheme
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Step 1: Get enhanced agentic context (RAG + query analysis)
      let enhancedContext = null;
      try {
        const agenticResponse = await axios.post(`${API}/navi/agentic/query`, {
          query: messageWithContext,
          mode: selectedMode,
          include_tools: true
        });
        
        if (agenticResponse.data.success) {
          enhancedContext = agenticResponse.data;
          setAgenticContext(enhancedContext);
          
          // Show document retrieval info
          if (enhancedContext.retrieval_context?.documents_found > 0) {
            toast.info(`Found ${enhancedContext.retrieval_context.documents_found} relevant documents`, {
              duration: 3000,
              icon: <FileText className="w-4 h-4" />
            });
          }
        }
      } catch (agenticErr) {
        console.error('Agentic context fetch failed:', agenticErr);
        // Continue with standard chat if agentic fails
      }

      // Step 2: Send to main NAVI chat endpoint with enhanced context
      const response = await axios.post(`${API}/navi/chat`, {
        message: messageWithContext,
        mode: selectedMode,
        context: {
          user_id: user?.id,
          company_name: user?.company_name,
          compliance_theme: themeContext?.name,
          theme_description: themeContext?.description,
          // Add agentic context if available
          agentic_context: enhancedContext ? {
            query_analysis: enhancedContext.query_analysis,
            retrieval_context: enhancedContext.retrieval_context,
            system_prompt: enhancedContext.system_prompt
          } : null
        }
      });

      const naviResponse = {
        id: (Date.now() + 1).toString(),
        content: response.data.response,
        isUser: false,
        timestamp: new Date().toISOString(),
        mode: response.data.mode,
        breachAnalysis: response.data.breach_analysis,
        // Store agentic context with the message for citation display
        agenticContext: enhancedContext ? {
          queryAnalysis: enhancedContext.query_analysis,
          citations: enhancedContext.retrieval_context?.citations || []
        } : null
      };

      // Show mode override notification if applicable
      if (response.data.mode_override) {
        toast.warning(response.data.mode_override, {
          duration: 5000,
          icon: <AlertTriangle className="w-4 h-4" />
        });
      }

      setMessages(prev => [...prev, naviResponse]);
    } catch {
      toast.error('Failed to get response from NAVI');
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        content: "I apologize, but I'm experiencing technical difficulties. As your compliance officer, I need to ensure our conversation is properly recorded. Please try again.",
        isUser: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const currentMode = NAVI_MODES.find(m => m.id === selectedMode);
  const currentTheme = selectedTheme ? COMPLIANCE_THEMES.find(t => t.id === selectedTheme) : null;

  return (
    <div className="flex min-h-screen bg-slate-950 overflow-hidden" data-testid="navi-page">
      <Sidebar />
      
      <div className="ml-64 flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-950 flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-5">
              <NaviAvatar size="xl" className="ring-4 ring-amber-500/20" />
              <div>
                <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
                  NAVI
                  <span className="px-2.5 py-1 bg-gradient-to-r from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-lg text-xs text-amber-400 font-medium">
                    AI Compliance Officer
                  </span>
                </h1>
                <p className="text-sm text-slate-400 mt-1">
                  Thinking compliance officer • Challenging regulator • Strategic advisor
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Mode Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-slate-700 hover:border-slate-600 bg-slate-800/50"
                    data-testid="navi-mode-selector"
                  >
                    {currentMode && <currentMode.icon className={`w-4 h-4 mr-2 text-${currentMode.color}-400`} />}
                    <span className={`text-${currentMode?.color || 'slate'}-400`}>{currentMode?.name || 'Select Mode'}</span>
                    <ChevronDown className="w-4 h-4 ml-2 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-72 bg-slate-900 border-slate-700 p-2">
                  {NAVI_MODES.map((mode) => (
                    <DropdownMenuItem
                      key={mode.id}
                      onClick={() => setSelectedMode(mode.id)}
                      className={`cursor-pointer rounded-lg p-3 ${selectedMode === mode.id ? 'bg-slate-800' : 'hover:bg-slate-800/50'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-${mode.color}-500/10 border border-${mode.color}-500/20`}>
                          <mode.icon className={`w-4 h-4 text-${mode.color}-400`} />
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium text-${mode.color}-400`}>{mode.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{mode.description}</div>
                        </div>
                        {selectedMode === mode.id && (
                          <div className={`w-2 h-2 rounded-full bg-${mode.color}-400 mt-1.5`} />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearHistory}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="clear-navi-history-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Clear
              </Button>
              
              {/* Knowledge Panel Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKnowledgePanel(!showKnowledgePanel)}
                className={`text-slate-400 hover:text-white hover:bg-slate-800 ${showKnowledgePanel ? 'bg-amber-500/20 text-amber-400' : ''}`}
                data-testid="knowledge-panel-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Knowledge
              </Button>
            </div>
          </div>
          
          {/* Agentic Context Banner */}
          {agenticContext?.query_analysis && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg"
            >
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-slate-400">Intent:</span>
                  <span className="text-amber-400 font-medium capitalize">{agenticContext.query_analysis.intent}</span>
                </div>
                {agenticContext.query_analysis.themes?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Themes:</span>
                    <span className="text-cyan-400">{agenticContext.query_analysis.themes.join(', ')}</span>
                  </div>
                )}
                {agenticContext.query_analysis.frameworks?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Frameworks:</span>
                    <span className="text-emerald-400">{agenticContext.query_analysis.frameworks.join(', ')}</span>
                  </div>
                )}
                {agenticContext.retrieval_context?.documents_found > 0 && (
                  <div className="flex items-center gap-2 ml-auto">
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400">{agenticContext.retrieval_context.documents_found} docs</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Compliance Themes */}
        <div className="p-4 border-b border-slate-800/50 bg-slate-900/30 flex-shrink-0 overflow-hidden">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <span className="text-xs text-slate-500 flex-shrink-0 font-medium mr-1">Theme:</span>
            
            {/* All Themes option */}
            <button
              onClick={() => setSelectedTheme(null)}
              disabled={loading}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs whitespace-nowrap transition-all disabled:opacity-50 ${
                selectedTheme === null
                  ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400'
                  : 'bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 hover:border-slate-600 text-slate-300 hover:text-white'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              All Topics
            </button>
            
            {COMPLIANCE_THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => setSelectedTheme(theme.id === selectedTheme ? null : theme.id)}
                disabled={loading}
                title={theme.description}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs whitespace-nowrap transition-all disabled:opacity-50 group ${
                  selectedTheme === theme.id
                    ? `bg-${theme.color}-500/20 border border-${theme.color}-500/40 text-${theme.color}-400`
                    : 'bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 hover:border-slate-600 text-slate-300 hover:text-white'
                }`}
              >
                <theme.icon className={`w-3.5 h-3.5 ${selectedTheme === theme.id ? `text-${theme.color}-400` : 'text-slate-400 group-hover:text-white'}`} />
                {theme.name}
              </button>
            ))}
          </div>
          
          {/* Selected theme description */}
          {selectedTheme && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2 px-2"
            >
              <p className="text-xs text-slate-400">
                <span className={`text-${COMPLIANCE_THEMES.find(t => t.id === selectedTheme)?.color}-400 font-medium`}>
                  {COMPLIANCE_THEMES.find(t => t.id === selectedTheme)?.name}:
                </span>{' '}
                {COMPLIANCE_THEMES.find(t => t.id === selectedTheme)?.description}
              </p>
            </motion.div>
          )}
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-6 bg-gradient-to-b from-slate-950 to-slate-900">
          {historyLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <NaviAvatar size="lg" className="mx-auto mb-4 animate-pulse" />
                <p className="text-slate-400 text-sm">Loading conversation...</p>
              </div>
            </div>
          ) : (
            <>
              <AnimatePresence mode="popLayout">
                {messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isUser={message.isUser}
                    mode={message.mode}
                    breachAnalysis={message.breachAnalysis}
                  />
                ))}
              </AnimatePresence>
              
              {loading && <TypingIndicator mode={selectedMode} />}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-slate-800 bg-slate-900/80 backdrop-blur-sm flex-shrink-0">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg"
            className="hidden"
          />
          
          {/* Selected file preview */}
          {selectedFile && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3 p-3 bg-slate-800/70 border border-slate-700 rounded-xl flex items-center gap-3"
            >
              {(() => {
                const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
                const fileInfo = SUPPORTED_FILE_TYPES[ext] || { icon: File, color: 'slate', name: 'File' };
                const IconComponent = fileInfo.icon;
                return (
                  <div className={`p-2 rounded-lg bg-${fileInfo.color}-500/10 border border-${fileInfo.color}-500/20`}>
                    <IconComponent className={`w-5 h-5 text-${fileInfo.color}-400`} />
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{selectedFile.name}</p>
                <p className="text-xs text-slate-400">
                  {(selectedFile.size / 1024).toFixed(1)} KB • Ready to upload
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4" />
              </Button>
            </motion.div>
          )}
          
          <div className="flex gap-3">
            {/* File upload button */}
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploadingFile}
              className="border-slate-700 hover:border-amber-500/50 hover:bg-amber-500/10 px-3"
              title="Upload document for NAVI to review"
              data-testid="navi-upload-btn"
            >
              <Paperclip className="w-5 h-5 text-slate-400 hover:text-amber-400" />
            </Button>
            
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  selectedFile
                    ? "Add a message about this document (optional)..."
                    : selectedMode === 'breach_response' 
                    ? "Describe the compliance incident or breach..." 
                    : selectedMode === 'assessment'
                    ? "Describe the control or process you want assessed..."
                    : currentTheme
                    ? `Ask about ${currentTheme.name.toLowerCase()}...`
                    : "Ask NAVI about your compliance requirements..."
                }
                disabled={loading}
                rows={1}
                className="w-full px-5 py-3.5 bg-slate-800/50 border border-slate-700 hover:border-slate-600 focus:border-amber-500/50 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
                style={{ minHeight: '52px', maxHeight: '150px' }}
                data-testid="navi-input"
              />
            </div>
            <Button
              onClick={() => handleSend()}
              disabled={(!input.trim() && !selectedFile) || loading}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-5 rounded-xl shadow-lg shadow-amber-500/20"
              data-testid="navi-send-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          
          {/* Mode and theme indicator */}
          <div className="flex items-center justify-between mt-3 px-1">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Zap className="w-3 h-3 text-amber-400" />
              <span>Mode: <span className={`text-${currentMode?.color || 'amber'}-400 font-medium`}>{currentMode?.name}</span></span>
              {currentTheme && (
                <>
                  <span className="text-slate-600">•</span>
                  <span>Theme: <span className={`text-${currentTheme.color}-400 font-medium`}>{currentTheme.name}</span></span>
                </>
              )}
            </div>
            <span className="text-xs text-slate-600">Press Enter to send • Shift+Enter for new line</span>
          </div>
        </div>
      </div>
      
      {/* Knowledge Panel Sidebar */}
      <AnimatePresence>
        {showKnowledgePanel && (
          <motion.div
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            className="w-80 flex-shrink-0 bg-slate-900 border-l border-slate-800 flex flex-col overflow-hidden"
          >
            {/* Panel Header */}
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-400" />
                <h3 className="font-semibold text-white">Knowledge Base</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKnowledgePanel(false)}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Query Analysis */}
              {agenticContext?.query_analysis && (
                <div>
                  <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Query Analysis</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Intent</span>
                      <span className="text-amber-400 capitalize">{agenticContext.query_analysis.intent}</span>
                    </div>
                    {agenticContext.query_analysis.themes?.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Themes</span>
                        <span className="text-cyan-400">{agenticContext.query_analysis.themes.length}</span>
                      </div>
                    )}
                    {agenticContext.query_analysis.frameworks?.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Frameworks</span>
                        <span className="text-emerald-400">{agenticContext.query_analysis.frameworks.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Retrieved Documents */}
              {agenticContext?.retrieval_context?.citations?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">
                    Retrieved Documents ({agenticContext.retrieval_context.citations.length})
                  </h4>
                  <div className="space-y-2">
                    {agenticContext.retrieval_context.citations.map((citation, idx) => (
                      <div key={idx} className="p-2 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className="text-sm text-white font-medium truncate">[{citation.index}] {citation.title}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">{citation.source}</span>
                          <span className="text-xs text-slate-500">{citation.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* SA Regulators */}
              {knowledgeBase?.south_african_regulators && (
                <div>
                  <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">SA Regulators</h4>
                  <div className="space-y-2">
                    {knowledgeBase.south_african_regulators.map((reg, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-800/30 rounded-lg">
                        <span className="text-white font-medium">{reg.code}</span>
                        <a 
                          href={reg.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-amber-400 hover:text-amber-300 text-xs"
                        >
                          Website
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Compliance Frameworks */}
              {knowledgeBase?.compliance_frameworks && (
                <div>
                  <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Frameworks</h4>
                  <div className="flex flex-wrap gap-2">
                    {knowledgeBase.compliance_frameworks.map((fw, idx) => (
                      <span 
                        key={idx} 
                        className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg"
                        title={fw.name}
                      >
                        {fw.code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Global Standards */}
              {knowledgeBase?.global_standard_setters && (
                <div>
                  <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Global Standards</h4>
                  <div className="flex flex-wrap gap-2">
                    {knowledgeBase.global_standard_setters.map((std, idx) => (
                      <span 
                        key={idx} 
                        className="text-xs px-2 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg"
                        title={std.focus_area}
                      >
                        {std.code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NaviPage;
